import { ConnectError } from '@bufbuild/connect';
import {
  ActionsPopup,
  deserify,
  getDifficultyLevelString,
  getMediaBasePath,
  IconWrapper,
  ImageWrapper,
  InfoBar,
  IStyles,
  Loader,
  PrimaryButton,
  pxToRem,
  pxTovW,
  QuestionContainerWithSolution,
  QuestionSourceTags,
  theme,
  useCommonServiceClientContext,
  calculateTotalTime,
} from '@geneo2-web/shared-ui';
import {
  Box,
  Divider,
  Input,
  Popover,
  TextareaAutosize,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  BloomLevelEnum,
  DifficultyLevelEnum,
  Module_ModuleCategoryEnum,
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { AssessmentCreationEnum } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import {
  QuestionModuleMap,
  QuestionSectionMeta,
  QuestionTypeMarks,
  SectionQuestionMetaData,
  TaskCreationStatusEnum,
  TaskEnum,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { EditWarningPopup } from 'apps/teacher/src/components/EditWarningPopup';
import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import { ChapterTopicPopup } from '../../../../components/ChapterTopicPopup';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../reduxStore/reduxHooks';
import {
  ASSESMENT_LIBRARY,
  ASSESSMENT_QUESTIONBANK,
  ASSESSMENT_REVIEW,
  HOME,
  QPG,
} from '../../../../routeHandling/RoutesNomenclature';
import {
  setToastInfo,
  setToastInfoV2,
} from '../../../Home/reducer/homeDashboard.slice';
import { BloomLevelComponent } from '../../components/BloomLevelComponent';
import SelectedChapters from '../../components/SelectedChapters';
import {
  setQPGAssessmentContent,
  setQPGAssessmentInstructionsHash,
  setQPGAssessmentTask,
  setQPGQuestionModuleMap,
  setQPGQuestionsSequenceInfo,
  setQPGQuestionsSequenceInfoHash,
  setQPGQuestionTypeMarks,
  setSelectedQuestionIndexForAlternateQuestion,
  setSelectedQuestionTypeForAlternateQuestion,
  setSelectedSectionIndexForAlternateQuestion,
} from '../../reducer/assessmentFlow.slice';
import { InteractiveButtons } from './InteractiveButtons';
import PrintableQuestionPaper from './PrintableQuestionPaper';
import {
  compressRanges,
  getAllIndexes,
  getBloomLevelData,
  getQuestionNumberFromIndex,
  getStringHash,
  numberToAlphabet,
  questionCountTypeWise,
  questionHasImage,
  useAddOrReplaceQPGQuestion,
  useDeleteQPGQuestion,
  useShowMediaClick,
} from './utils';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';
import { interactionEvent } from '../../../Auth/auth.events';
import { InfoBarComponent } from '../../components/InfoBarComp';
import EditMarksPanel from './EditMarksPanel';
import { set } from 'date-fns';
import { Timestamp } from '@bufbuild/protobuf';
import { setModeOfConduction } from '../../../ManageHomework/reducer/manageHomework.slice';
import { getModeOfConductionEnum } from 'apps/teacher/src/utils/functions';
import { useWhiteLabeledUrl } from 'apps/teacher/src/utils/customHooks';

const styles: IStyles = {
  root: {
    backgroundColor: 'neutral.paleGrey',
    // height: '100vh',
    marginBottom: pxTovW(10),
    // maxHeight: {
    //   xs: `calc(100vh - ${pxToRem(60)})`,
    //   md: `calc(100vh - ${pxTovW(100)})`,
    // },
    display: 'flex',
    flexDirection: 'column',
    overflow: { md: 'hidden', xs: 'auto' },
  },
  mainContainer: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { md: pxTovW(40) },
    padding: { md: pxToRem(20), lg: `${pxTovW(15)} ${pxTovW(240)}` },
    paddingBottom: { xs: pxToRem(50), md: pxToRem(20) },
    height: '100%',
  },
  leftPanel: {
    flexBasis: { md: pxTovW(550), lg: pxTovW(400) },
    display: 'flex',
    flexDirection: 'column',
    gap: { md: pxTovW(30) },
  },
  rightPanel: {
    flexBasis: { md: pxTovW(1000) },
    paddingBottom: { xs: pxToRem(70), md: 0 },
    flexGrow: 1,
  },
  mobileContainer: {
    display: { xs: 'flex', md: 'none' },
    border: '1px solid #007CDC4D',
    flexDirection: 'row',
    gap: { xs: pxToRem(5), md: pxTovW(5) },
    padding: { xs: pxToRem(10), md: pxTovW(15) },
    marginY: { xs: pxToRem(10), md: pxTovW(10) },
    // marginX: { xs: pxToRem(20) },
    background: '#FFFFFF',
    boxShadow: `0px 0px ${pxToRem(5)} #E7E7E7D9`,
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
    justifyContent: 'space-between',
  },
  BloomContainer: {
    border: '1px solid #E0E2E4',
    display: { xs: 'none', md: 'flex' },
    flexDirection: 'column',
    gap: { xs: pxToRem(11), md: pxTovW(20) },
    padding: {
      xs: pxToRem(10),
      md: `${pxTovW(15)} ${pxTovW(13)} ${pxTovW(15)} ${pxTovW(10)}`,
    },
    background: '#FFFFFF',
    boxShadow: `0px 0px ${pxToRem(5)} #E7E7E7D9`,
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
  },
  AssessmentNameContainer: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #E0E2E4',
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(11), md: pxTovW(20) },
    padding: {
      xs: pxToRem(10),
      md: `${pxTovW(15)} ${pxTovW(13)} ${pxTovW(15)} ${pxTovW(10)}`,
    },
    background: '#FFFFFF',
    boxShadow: `0px 0px ${pxToRem(5)} #E7E7E7D9`,
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
  },
};

type WarningPopupState = 'home' | 'back' | undefined;

export default function ReviewQuestionPaper() {
  const { assessment_id } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const [selectedopen, setSelectedOpen] = useState(false);
  const [bloomOpen, setBloomOpen] = useState(false);
  const [editMarksOpen, setEditMarksOpen] = useState(false);
  const navigate = useNavigate();
  const {
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const { class_subject_info } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );
  const location = useLocation();
  const fromPath = location.state?.from;
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const {
    qpg_assessment_task,
    qpg_assessment_content,
    qpg_questions_sequence_info,
    qpg_question_type_marks,
    qpg_questions_sequence_info_hash,
    qpg_assessment_instructions_hash,
    qpg_question_module_map,
    qpg_format_details,
  } = deserify(useAppSelector((state) => state.assessment));
  const dispatch = useAppDispatch();
  const questionModuleMap = qpg_question_module_map || [];

  const qpgAssessmentTask = new AssessmentTask(qpg_assessment_task);
  const qpgAssessmentQuestions =
    qpg_assessment_content?.map((ques) => new Question(ques)) || [];
  const [assessmentName, setAssessmentName] = useState(
    qpgAssessmentTask.assessmentTitle || ''
  );
  const [assessmentNameEditMode, setAssessmentNameEditMode] = useState(false);
  const qpgQuestionsSequenceInfo = new TaskInfo(qpg_questions_sequence_info);
  const qpgQuestionTypeMarks = qpg_question_type_marks || [];
  const taskQuestionInfo = qpgQuestionsSequenceInfo.taskQuestionInfo || [];
  const taskCreationStatus = qpgAssessmentTask.taskCreationStatus;
  const questionSectionMetaInfo =
    qpgQuestionsSequenceInfo.questionSectionMetaInfo.sort(
      (a, b) => a.sectionNo - b.sectionNo
    ) || [];
  const testLevelInstructions = qpgAssessmentTask.assessmentInstructions || [];
  const [updatingAssessment, setUpdatingAssessment] = useState(false);
  const [deleteQuestionPopupState, setDeleteQuestionPopupState] = useState<
    | {
        sectionIndex: number;
        questionIndex?: number;
        altIdx?: number;
      }
    | undefined
  >(undefined);
  const [hasCustomInstructions, setHasCustomInstructions] = useState(
    testLevelInstructions.length > 0 && testLevelInstructions[0].length > 0
  );
  const hasAiGeneratedQuestions =
    qpgAssessmentTask.isAiAssessment ||
    questionSectionMetaInfo.some((info) =>
      info.sectionQuestionsInfo.some(
        (ques) =>
          ques.isAiGenerated ||
          ques.alternateQuestions.some((alt) => alt.isAiGenerated)
      )
    );
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [printAnswer, setPrintAnswer] = useState(false);
  const handlePopoverClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const [loading, setLoading] = useState<'loading' | 'success' | 'error'>(
    'success'
  );
  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();
  const [warningPopupOpen, setWarningPopupOpen] =
    useState<WarningPopupState>(undefined);

  const deleteQuestion = useDeleteQPGQuestion();
  const addOrReplaceQuestion = useAddOrReplaceQPGQuestion();
  const showMediaClick = useShowMediaClick();
  const school_logo_url = useWhiteLabeledUrl();
  const handleBackClick = (isHome?: boolean) => {
    if (isHome) {
      navigate(HOME);
      return;
    } else {
      if (location.state?.redirectionPath) {
        navigate(location.state.redirectionPath, {
          state: {
            fromQPG: true,
          },
        });
        return;
      }
    }
    navigate(-1);
  };

  const showWarningPopup = (val: WarningPopupState) => {
    setWarningPopupOpen(val);
  };

  const generateHashString = (taskInfo?: TaskInfo) => {
    return getStringHash(new TaskInfo(taskInfo)?.toJsonString() || '');
  };

  // --------------------------------------------------------------

  const isAssessmentChanged =
    qpg_questions_sequence_info_hash !==
    generateHashString(qpgQuestionsSequenceInfo);

  useEffect(() => {
    if (
      isAssessmentChanged &&
      qpg_questions_sequence_info_hash !== undefined &&
      taskCreationStatus !==
        TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
    ) {
      // Update the assessment with the new changes
      // handleSave(false, false, false, false, false, qpg_question_type_marks);
      handleSave();
    }
  }, [isAssessmentChanged]);

  useEffect(() => {
    if (updatingAssessment) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [updatingAssessment]);

  // --------------------------------------------------------------

  const getFormattedQuestionFromId = (id: string) => {
    const ques = qpgAssessmentQuestions.find((ques) => ques.questionId == id);
    const taskInfo = taskQuestionInfo.find((info) => info.questionId == id);
    if (ques?.question?.model.value?.commonQuestionContent) {
      const quesType = ques.questionType;
      const foundQuesType = qpgQuestionTypeMarks.find(
        (qType) => qType.questionType == quesType
      );
      if (foundQuesType) {
        ques.question.model.value.commonQuestionContent.marks = [
          foundQuesType.marks || 0,
        ];
      } else if (typeof taskInfo?.maxMarks === 'number') {
        ques.question.model.value.commonQuestionContent.marks = [
          taskInfo.maxMarks,
        ];
      }
      if (typeof taskInfo?.estimatedTimeInMin === 'number') {
        ques.question.model.value.commonQuestionContent.time =
          taskInfo.estimatedTimeInMin * 60;
      }
    }
    return ques;
  };

  const questionsList = (includeAlternateQuestions = false) =>
    questionSectionMetaInfo
      .map((info) =>
        info.sectionQuestionsInfo.map((quesList) => [
          getFormattedQuestionFromId(quesList.questionId),
          ...(includeAlternateQuestions
            ? quesList.alternateQuestions.map((altQuestion) =>
                getFormattedQuestionFromId(altQuestion.questionId)
              )
            : []),
        ])
      )
      .flat()
      .flat()
      .filter((val) => val !== undefined) as Question[];
  const questionsListWithoutAlternateQuestions = questionsList(false);
  const questionsListWithAlternateQuestions = questionsList(true);
  const [replacedQuestionIds, setReplacedQuestionIds] = useState<string[]>([]);

  const totalQuestionsCount =
    questionsListWithoutAlternateQuestions?.length || 0;
  const totalTimeInMinutes = calculateTotalTime(
    questionsListWithoutAlternateQuestions || []
  );
  const totalMarks =
    questionsListWithoutAlternateQuestions?.reduce((acc, ques) => {
      if (
        Array.isArray(ques?.question?.model.value?.commonQuestionContent?.marks)
      ) {
        return (
          acc +
          (ques?.question?.model.value?.commonQuestionContent?.marks[0] || 0)
        );
      }
      return acc;
    }, 0) || 0;
  interface IDifficultyLevelBreakdown {
    easy: {
      difficultyLevel: DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW;
      count: number;
    };
    medium: {
      difficultyLevel: DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM;
      count: number;
    };
    hard: {
      difficultyLevel: DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH;
      count: number;
    };
  }
  const getDifficultyLevelBreakdown = (
    questionModuleMap: QuestionModuleMap[]
  ) => {
    const difficultyLevelBreakdown: IDifficultyLevelBreakdown = {
      easy: {
        difficultyLevel: DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW,
        count: 0,
      },
      medium: {
        difficultyLevel: DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM,
        count: 0,
      },
      hard: {
        difficultyLevel: DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH,
        count: 0,
      },
    };
    questionModuleMap.forEach((questionModule) => {
      const difficultyLevel = questionModule.difficultyLevel;
      switch (difficultyLevel) {
        case DifficultyLevelEnum.DIFFICULTY_LEVEL_LOW:
          ++difficultyLevelBreakdown.easy.count;
          break;
        case DifficultyLevelEnum.DIFFICULTY_LEVEL_MEDIUM:
          ++difficultyLevelBreakdown.medium.count;
          break;
        case DifficultyLevelEnum.DIFFICULTY_LEVEL_HIGH:
          ++difficultyLevelBreakdown.hard.count;
          break;
        default:
          break;
      }
    });
    return difficultyLevelBreakdown;
  };

  const bloomLevelData = getBloomLevelData(
    questionsListWithoutAlternateQuestions
  );

  const infoBarContentList = [
    {
      iconName: 'questions',
      heading: totalQuestionsCount,
      subHeading: 'Questions',
    },
    {
      iconName: 'clock',
      heading: `${totalTimeInMinutes}`,
      subHeading: 'Minutes',
    },
    {
      iconName: 'level',
      heading: `${getDifficultyLevelString(qpgAssessmentTask.difficultyLevel)}`,
      subHeading: 'Difficulty Level',
    },
  ];
  const getDifficultyLevelstats = () => {
    const breakdown = getDifficultyLevelBreakdown(
      qpg_question_module_map || []
    );
    return [
      {
        iconName: 'stat-green',
        heading: breakdown.easy.count,
        headingSubText: 'Qs',
        subHeading: 'Easy',
      },
      {
        iconName: 'stat-yellow',
        heading: breakdown.medium.count,
        headingSubText: 'Qs',
        subHeading: 'Medium',
      },
      {
        iconName: 'stat-red',
        heading: breakdown.hard.count,
        headingSubText: 'Qs',
        subHeading: 'Hard',
      },
    ];
  };
  // --------------------------------------------------------------

  const renderQuestionTypeList = () => {
    return Object.keys(
      questionCountTypeWise(questionsListWithoutAlternateQuestions)
    ).map((val, index) => (
      <Box
        key={index}
        sx={{
          display: 'flex',
          gap: { xs: pxToRem(5), md: pxTovW(5) },
        }}
      >
        <IconWrapper
          name="question-blue"
          type="png"
          parentFolder="icons"
          size="md"
        />
        <Typography variant="h5">{val}</Typography>
      </Box>
    ));
  };

  // --------------------------------------------------------------

  useEffect(() => {
    if (assessment_id) {
      if (!location.state?.isFromQuestionBank) {
        // if not coming from question bank
        fetchAssessmentContent(assessment_id);
      } else {
        // resets the location state so that on refresh fetch assessment content is called again
        navigate(location.pathname, {
          replace: true,
          state: {
            isFromQuestionBank: false,
            redirectionPath: location.state?.redirectionPath,
          },
        });
      }
      dispatch(setSelectedSectionIndexForAlternateQuestion(undefined));
      dispatch(setSelectedQuestionIndexForAlternateQuestion(undefined));
      dispatch(setSelectedQuestionTypeForAlternateQuestion(undefined));
    }
  }, [assessment_id]);

  useEffect(() => {
    setSelectedFunction(() =>
      isAssessmentChanged ? () => showWarningPopup('back') : handleBackClick
    );
    setSelectedLogoClickHandler(() =>
      isAssessmentChanged
        ? () => showWarningPopup('home')
        : () => handleBackClick(true)
    );
    return () => {
      setSelectedFunction(null);
      setSelectedLogoClickHandler(null);
    };
  }, [warningPopupOpen, isAssessmentChanged]);

  // --------------------------------------------------------------

  const fetchAssessmentContent = async (assessment_id: string) => {
    try {
      setLoading('loading');
      const response =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: user_info?.teacherProfileId,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            assessmentId: (assessment_id ? BigInt(assessment_id) : undefined),
          }
        );
      dispatch(setQPGAssessmentTask(response.data?.assessment));
      dispatch(setQPGAssessmentContent(response.data?.assessmentContent));
      dispatch(
        setQPGQuestionsSequenceInfo(response.data?.questionsSequenceInfo)
      );
      dispatch(
        setQPGQuestionsSequenceInfoHash(
          generateHashString(new TaskInfo(response.data?.questionsSequenceInfo))
        )
      );
      dispatch(setQPGQuestionModuleMap(response.data?.questionModuleMap));
      const instructions =
        response.data?.assessment?.assessmentInstructions || [];
      dispatch(
        setQPGAssessmentInstructionsHash(getStringHash(instructions.join('\n')))
      );
      setHasCustomInstructions(
        instructions.length > 0 && instructions[0].length > 0
      );
      setAssessmentName(response.data?.assessment?.assessmentTitle || '');
      dispatch(setQPGQuestionTypeMarks(response.data?.questionTypeAndMarks));
      setLoading('success');
    } catch (error) {
      dispatch(setQPGAssessmentTask(undefined));
      dispatch(setQPGAssessmentContent(undefined));
      dispatch(setQPGQuestionsSequenceInfo(undefined));
      dispatch(setQPGQuestionTypeMarks(undefined));
      dispatch(setSelectedSectionIndexForAlternateQuestion(undefined));
      dispatch(setSelectedQuestionIndexForAlternateQuestion(undefined));
      dispatch(setSelectedQuestionTypeForAlternateQuestion(undefined));
      setLoading('error');
    }
  };

  // --------------------------------------------------------------

  const getSectionStatementForInstruction = (
    sectionQuestionsInfo: SectionQuestionMetaData[],
    sectionIndex: number
  ) => {
    const sectionQuestions = (sectionQuestionsInfo || [])
      .map((ques) => {
        const quesId =
          ques.questionId ||
          ques.alternateQuestions.find((val) => !!val.questionId)?.questionId;
        return quesId ? getFormattedQuestionFromId(quesId) : undefined;
      })
      .filter((val) => !!val) as Question[];
    const allQuestionsType = questionCountTypeWise(sectionQuestions);
    return `${Object.keys(allQuestionsType)
      ?.map((quesKey) => {
        const quesType = allQuestionsType[quesKey];
        const { type, marks, count } = quesType;
        const indexes = getAllIndexes(
          sectionQuestions,
          (ques) => ques.questionType == type
        );
        const compressedIndexes = compressRanges(indexes);
        const questionNumbers = compressedIndexes.map((range) => {
          return range.map((index) =>
            getQuestionNumberFromIndex(
              sectionIndex,
              index,
              questionSectionMetaInfo
            )
          );
        });
        const fromOrFor = questionNumbers[0].length > 1 ? 'from' : 'for';
        const questionNumbersString = questionNumbers
          .map((num) => num.join(' to '))
          .join(', ');
        const marksEach =
          (count > 0 ? Math.round(marks / count) : undefined) || 0;
        const marksStatement = marksEach > 1 ? 'marks' : 'mark';
        const questionsOrQuestion =
          questionNumbers[0].length > 1 ? 'questions' : 'question';
        switch (type) {
          case Question_QuestionEnum.QUESTION_TYPE_MCQS:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has MCQ single ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_MCQM:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has MCQ multiple ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_FIB:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has Fill in the blanks ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_MTF:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has Match the following ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_ARRANGE:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has Arrange the following ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_TF:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has True-False ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has Very short answer ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_SHORT:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has Short answer ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_LONG:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has Very long answer ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_LABEL:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has Label ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_CBQ:
            return `Section ${numberToAlphabet(
              sectionIndex + 1
            )} has Competency Based ${questionsOrQuestion} ${fromOrFor} Q No. ${questionNumbersString}, for ${marksEach} ${marksStatement} each`;
          default:
            return '';
        }
      })
      .join(', ')}.`;
  };

  // --------------------------------------------------------------

  const generalInstructions =
    questionSectionMetaInfo.length > 0
      ? [
          `This question paper has ${questionSectionMetaInfo.length} sections.`,
          ...(questionSectionMetaInfo.map((info, index) => {
            return getSectionStatementForInstruction(
              info.sectionQuestionsInfo.sort((a, b) => a.sequence - b.sequence),
              index
            );
          }) || []),
        ]
      : [];

  // --------------------------------------------------------------

  const renderChapterTopicPopup = () => {
    return (
      <ChapterTopicPopup
        open={selectedopen}
        handleClose={() => {
          setSelectedOpen(false);
        }}
        popupBody={
          <SelectedChapters
            // editClickHandler={() => {
            //   console.log('editClickHandler');
            // }}
            questionModuleMap={questionModuleMap
              .filter((val) =>
                questionsListWithoutAlternateQuestions.some(
                  (ques) => ques.questionId === val.questionId
                )
              )
              .filter(
                (val, index, self) =>
                  index ===
                  self.findIndex((t) => t.questionId === val.questionId)
              )}
            assessmentTaskInfo={qpgAssessmentTask.taskMetaInfo}
          />
        }
      />
    );
  };

  // --------------------------------------------------------------

  const renderBloomLevelPopup = () => {
    return (
      <ChapterTopicPopup
        open={bloomOpen}
        handleClose={() => {
          setBloomOpen(false);
        }}
        popupBody={
          <Box
            sx={{
              ...styles.BloomContainer,
              display: { xs: 'flex', md: 'none' },
            }}
          >
            <Box>
              <BloomLevelComponent bloomLevelData={bloomLevelData} />
            </Box>
            <Box>
              <InfoBar contentList={infoBarContentList} />
            </Box>
            <Box>
              <Typography
                variant="h3"
                fontWeight={600}
                fontFamily={'poppins'}
                marginBottom={pxToRem(10)}
              >
                Difficulty Level Breakdown
              </Typography>
              <InfoBar contentList={getDifficultyLevelstats()} />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                marginY: { xs: pxToRem(10) },
                gap: { xs: pxToRem(10) },
              }}
            >
              <Typography variant="h3" fontWeight={600} fontFamily={'poppins'}>
                Question Type
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: pxToRem(12) },
                }}
              >
                {renderQuestionTypeList()}
              </Box>
            </Box>
          </Box>
        }
      />
    );
  };
  const renderEditMarksPopup = () => {
    return (
      <ChapterTopicPopup
        open={editMarksOpen}
        handleClose={() => {
          setEditMarksOpen(false);
        }}
        popupBody={
          <Box
            sx={{
              ...styles.BloomContainer,
              display: { xs: 'flex', md: 'none' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: {
                  xs: pxToRem(20),
                  md: pxTovW(20),
                },
              }}
            >
              <EditMarksPanel
                questions={questionsListWithoutAlternateQuestions}
                isMobile={isMobile}
                onSave={handleMarksSave}
              />
            </Box>
          </Box>
        }
      ></ChapterTopicPopup>
    );
  };

  // --------------------------------------------------------------

  const renderTestLevelInstructions = () => {
    return (
      <Box
        sx={{
          border: {
            md: '1px solid #E0E2E4',
            xs: '1px solid #007CDC4D',
          },
          borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
          background: '#FFFFFF',
          boxShadow: `0px 0px ${pxToRem(5)} #E7E7E7D9`,
          padding: {
            md: pxTovW(20),
            xs: pxToRem(10),
          },
          display: 'flex',
          flexDirection: 'column',
          gap: { md: pxTovW(10), xs: pxToRem(10) },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant={isMobile ? 'h2' : 'h3'}
            sx={{
              color: {
                xs: '#000000',
                md: '#007CDC',
              },
            }}
          >
            {hasCustomInstructions
              ? 'Test Instructions'
              : 'General Instructions'}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: pxToRem(5), md: pxTovW(10) },
            }}
          >
            {taskCreationStatus !==
              TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED && (
              <>
                {hasCustomInstructions && (
                  <PrimaryButton
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: { xs: pxToRem(40), md: pxTovW(50) },
                      height: { xs: pxToRem(20), md: pxTovW(23) },
                      borderRadius: { xs: pxToRem(5), md: pxTovW(5) },
                    }}
                    disabled={
                      testLevelInstructions.join('\n').length === 0 ||
                      qpg_assessment_instructions_hash ===
                        getStringHash(testLevelInstructions.join('\n'))
                    }
                    onClick={async () => {
                      // save the instructions
                      handleSave(true);
                      await interactionEvent({
                        context: hasCustomInstructions
                          ? 'Test_instructions'
                          : 'General_instructions',
                        name: 'SAVE',
                        pathSegments: pathSegments,
                        isOffline: isOffline,
                      });
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: 'white',
                      }}
                    >
                      Save
                    </Typography>
                  </PrimaryButton>
                )}
                <IconWrapper
                  name={hasCustomInstructions ? 'delete2' : 'edit'}
                  type="png"
                  parentFolder="icons"
                  size="large"
                  customSx={{
                    height: { xs: pxToRem(23) },
                    width: { xs: pxToRem(23) },
                    cursor: 'pointer',
                  }}
                  onClick={async () => {
                    if (hasCustomInstructions) {
                      // delete the instructions
                      await handleSave(true, false, true);
                    }
                    setHasCustomInstructions(!hasCustomInstructions);
                    await interactionEvent({
                      context: hasCustomInstructions
                        ? 'Test_instructions'
                        : 'General_instructions',
                      name: hasCustomInstructions ? 'DELETE' : 'EDIT',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  }}
                />
              </>
            )}
            {isMobile && !hasCustomInstructions ? (
              <IconWrapper
                name={'down-arrow'}
                type="png"
                parentFolder="icons"
                size="large"
                customSx={{
                  height: { xs: pxToRem(15) },
                  width: { xs: pxToRem(15) },
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setInstructionsOpen(!instructionsOpen);
                }}
              />
            ) : (
              <></>
            )}
          </Box>
        </Box>
        {hasCustomInstructions ? (
          <Box
            sx={{
              width: '100%',
              // height: '100%',
              '& textarea': {
                width: '100%',
                // height: '100%',
                border: 'none',
                outline: 'none',
                '&:focus': {
                  outline: 'none',
                },
                fontFamily: 'Lato',
                color: '#333333',
                fontWeight: 400,
                fontSize: {
                  xs: pxToRem(15),
                  md: pxToRem(14),
                  lg: pxTovW(21),
                },
                resize: 'none',
              },
            }}
          >
            <TextareaAutosize
              value={testLevelInstructions.join('\n')}
              placeholder="Type your instructions here"
              onChange={(e) => {
                qpgAssessmentTask.assessmentInstructions = [e.target.value];
                dispatch(setQPGAssessmentTask(qpgAssessmentTask));
              }}
              disabled={
                updatingAssessment ||
                taskCreationStatus ===
                  TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
              }
              minRows={2}
            />
          </Box>
        ) : generalInstructions.length > 0 && isMobile ? (
          <ChapterTopicPopup
            open={instructionsOpen}
            handleClose={() => {
              setInstructionsOpen(false);
            }}
            mainContainerSx={{
              paddingX: { xs: pxToRem(10), md: pxTovW(15) },
              paddingY: { xs: pxToRem(25), md: pxTovW(15) },
            }}
            popupBody={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant={'h1'}
                    sx={{
                      color: '#007CDC',
                    }}
                  >
                    {hasCustomInstructions
                      ? 'Test Instructions'
                      : 'General Instructions'}
                  </Typography>
                </Box>
                <Divider sx={{ bgcolor: '#E0E0E0', height: '1px', my: 1 }} />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: pxToRem(10),
                  }}
                >
                  {generalInstructions.map((instruction, index) => (
                    <Typography key={index} variant="bodyText">
                      {index + 1}. {instruction}
                    </Typography>
                  ))}
                </Box>
              </Box>
            }
          />
        ) : (
          generalInstructions.map((instruction, index) => (
            <Typography key={index} variant="bodyText">
              {index + 1}. {instruction}
            </Typography>
          ))
        )}
      </Box>
    );
  };

  // --------------------------------------------------------------

  // Handler for drag-and-drop
  const onDragEnd = (result: any, sectionIndex?: number) => {
    if (!result.destination) return;
    const { source, destination, type } = result;
    // Deep clone to avoid mutation
    const sectionMeta =
      qpgQuestionsSequenceInfo.questionSectionMetaInfo.sort(
        (a, b) => a.sectionNo - b.sectionNo
      ) || [];
    if (!sectionMeta) return;

    // Level 1: Reorder questions within a section
    if (type === 'MAIN_BOX' && typeof sectionIndex === 'number') {
      const section = questionSectionMetaInfo[
        sectionIndex
      ] as QuestionSectionMeta;
      if (!section) return;
      const questions = (
        Array.from(
          section.sectionQuestionsInfo ?? []
        ) as SectionQuestionMetaData[]
      ).sort((a, b) => a.sequence - b.sequence);
      const [removed] = questions.splice(source.index, 1);
      questions.splice(destination.index, 0, removed);
      // Update sequence field if needed
      questions.forEach((q: SectionQuestionMetaData, idx: number) => {
        if (q) q.sequence = idx;
      });
      section.sectionQuestionsInfo = questions;
      sectionMeta[sectionIndex] = section;
      qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
      dispatch(setQPGQuestionsSequenceInfo(qpgQuestionsSequenceInfo));
    }
    // Level 2: Reorder alternates within a question
    else if (type.startsWith('ALTERNATE_QUESTION')) {
      // type: ALTERNATE_QUESTION-<sectionIndex>-<questionIndex>
      const [_, secIdx, quesIdx] = type.split('-');
      const secIdxNum = Number(secIdx);
      const quesIdxNum = Number(quesIdx);
      if (isNaN(secIdxNum) || isNaN(quesIdxNum)) return;
      const section = sectionMeta[secIdxNum] as QuestionSectionMeta;
      if (!section) return;
      const quesMeta = section.sectionQuestionsInfo[
        quesIdxNum
      ] as SectionQuestionMetaData;
      if (!quesMeta) return;
      // Combine main + alternates for reordering
      const outerSequence = quesMeta.sequence;
      const allQuestions = [
        quesMeta,
        ...quesMeta.alternateQuestions.sort((a, b) => a.sequence - b.sequence),
      ].map((val) => {
        val.alternateQuestions = [];
        return val;
      });
      const [removed] = allQuestions.splice(source.index, 1);
      allQuestions.splice(destination.index, 0, removed);
      // After reorder: first element is new main, rest are alternates
      const newSectionQuestionMetaData = new SectionQuestionMetaData({
        ...allQuestions[0],
        alternateQuestions: allQuestions.slice(1).map((val, index) => ({
          ...val,
          sequence: index + 1,
        })),
        sequence: outerSequence,
      });
      section.sectionQuestionsInfo[quesIdxNum] = newSectionQuestionMetaData;
      sectionMeta[secIdxNum] = section;
      qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
      dispatch(setQPGQuestionsSequenceInfo(qpgQuestionsSequenceInfo));
    }
  };

  // --------------------------------------------------------------

  const renderOrBox = () => {
    return (
      <Box sx={{ px: { xs: pxToRem(10), md: pxTovW(20) } }}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#DDF0FF',
              color: '#000',
              padding: '10px',
              border: '1px solid #007CDC',
              borderRadius: '50%',
              zIndex: 1,
            }}
          >
            OR
          </Box>

          <Box
            sx={{
              height: '1px',
              width: '100%',
              backgroundColor: '#007CDC',
              position: 'absolute',
              top: '50%',
              left: '0',
              right: '0',
              transform: 'translateY(-50%)',
            }}
          />
        </Box>
      </Box>
    );
  };

  // --------------------------------------------------------------

  const handleReplaceClick = async (
    sectionIndex: number,
    index: number,
    altIdx: number,
    currentQuestion?: Question,
    allowAiQuestions?: boolean,
    prompts?: string[]
  ) => {
    if (!currentQuestion) return;
    try {
      setUpdatingAssessment(true);

      // Get bloom level and difficulty level from current question
      const questionBloomLevels = currentQuestion.questionMeta?.bloomLevel
        ? [currentQuestion.questionMeta?.bloomLevel]
        : undefined;
      const requiredDifficultyTypes = currentQuestion.questionMeta
        ?.difficultyLevel
        ? [currentQuestion.questionMeta.difficultyLevel]
        : [];

      // Get all module IDs for fallback
      const allModuleIds =
        qpgAssessmentTask.taskMetaInfo?.chapterInfo?.chapterTopicsInfo
          .flatMap((chapter) => [
            chapter.chapterId,
            ...chapter.topicInfo.map((topic) => topic.topicId),
          ])
          ?.sort((a, b) => a - b) || [];

      // Get the moduleId from questionModuleMap
      const questionMapEntry = questionModuleMap.find(
        (q) => q.questionId === currentQuestion.questionId
      );
      let moduleQuestions;

      // Attempt 1: specific moduleId + bloom level of the question
      if (
        questionMapEntry &&
        (questionMapEntry.topicId || questionMapEntry.chapterId)
      ) {
        const specificModuleId =
          questionMapEntry.topicId ?? questionMapEntry.chapterId;
        if (specificModuleId) {
          moduleQuestions =
            await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
              {
                teacherId: user_info?.teacherProfileId,
                subjectId: qpgAssessmentTask.subjectId,
                sectionId: qpg_format_details?.selectedSection?.id,
                moduleIds: [specificModuleId],
                assessmentCreationType:
                  AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
                noOfQuestions: 1,
                requiredQuestionTypes: [currentQuestion.questionType],
                requiredDifficultyTypes: requiredDifficultyTypes,
                bloomLevels: questionBloomLevels,
                qIdsToBeIgnored: [
                  ...questionsListWithAlternateQuestions.map(
                    (ques) => ques.questionId || ''
                  ),
                  ...replacedQuestionIds,
                ],
                currentQuestionId: currentQuestion.questionId,
                allowAiQuestions: allowAiQuestions,
                aiQuestionPrompts: allowAiQuestions ? prompts || [] : [],
                isAiAssessment: allowAiQuestions,
              }
            );

          // If no questions found, reset to trigger fallback
          if (
            !moduleQuestions.data?.questions ||
            moduleQuestions.data.questions.length === 0
          ) {
            moduleQuestions = undefined;
          }
        }
      }

      // Fallback 1: bloom level of that question with all module ids
      if (
        (!moduleQuestions || !moduleQuestions.data?.questions?.length) &&
        questionBloomLevels
      ) {
        moduleQuestions =
          await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
            {
              teacherId: user_info?.teacherProfileId,
              subjectId: qpgAssessmentTask.subjectId,
              sectionId:
                qpg_format_details?.selectedSection?.id ||
                class_subject_info?.sectionId,
              moduleIds: allModuleIds,
              assessmentCreationType:
                AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
              noOfQuestions: 1,
              requiredQuestionTypes: [currentQuestion.questionType],
              requiredDifficultyTypes: requiredDifficultyTypes,
              bloomLevels: questionBloomLevels,
              qIdsToBeIgnored: [
                ...questionsListWithAlternateQuestions.map(
                  (ques) => ques.questionId || ''
                ),
                ...replacedQuestionIds,
              ],
              currentQuestionId: currentQuestion.questionId,
              allowAiQuestions: allowAiQuestions,
              aiQuestionPrompts: allowAiQuestions ? prompts || [] : [],
              isAiAssessment: allowAiQuestions,
            }
          );

        // If no questions found, reset to trigger fallback 2
        if (
          !moduleQuestions.data?.questions ||
          moduleQuestions.data.questions.length === 0
        ) {
          moduleQuestions = undefined;
        }
      }

      // Fallback 2: no bloom level + all module ids
      if (!moduleQuestions || !moduleQuestions.data?.questions?.length) {
        moduleQuestions =
          await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
            {
              teacherId: user_info?.teacherProfileId,
              subjectId: qpgAssessmentTask.subjectId,
              sectionId:
                qpg_format_details?.selectedSection?.id ||
                class_subject_info?.sectionId,
              moduleIds: allModuleIds,
              assessmentCreationType:
                AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
              noOfQuestions: 1,
              requiredQuestionTypes: [currentQuestion.questionType],
              requiredDifficultyTypes: requiredDifficultyTypes,
              qIdsToBeIgnored: [
                ...questionsListWithAlternateQuestions.map(
                  (ques) => ques.questionId || ''
                ),
                ...replacedQuestionIds,
              ],
              currentQuestionId: currentQuestion.questionId,
              allowAiQuestions: allowAiQuestions,
              aiQuestionPrompts: allowAiQuestions ? prompts || [] : [],
              isAiAssessment: allowAiQuestions,
            }
          );
      }

      if (moduleQuestions && moduleQuestions.data?.questions?.length) {
        const newQuestion = moduleQuestions.data.questions[0];
        if (
          currentQuestion.questionId &&
          !replacedQuestionIds.includes(currentQuestion.questionId)
        ) {
          setReplacedQuestionIds([
            ...replacedQuestionIds,
            currentQuestion.questionId,
          ]);
        }
        await addOrReplaceQuestion(
          newQuestion,
          sectionIndex,
          index,
          altIdx,
          moduleQuestions.data?.questionModuleMap
        );
        const newQuestionModuleMapData =
          moduleQuestions.data?.questionModuleMap?.find(
            (val) => val.questionId === newQuestion.questionId
          );
        if (newQuestionModuleMapData) {
          const newModuleMap = [
            ...questionModuleMap,
            newQuestionModuleMapData,
          ].filter((val) => val.questionId !== currentQuestion.questionId);
          if (newQuestionModuleMapData) {
            dispatch(setQPGQuestionModuleMap(newModuleMap));
          }
        }
        dispatch(
          setToastInfo({
            label: 'Question replaced successfully',
            variant: 'info',
            open: true,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            label: 'No questions found to replace',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err) {
      let message = 'Failed to replace question';
      if (err instanceof ConnectError) {
        message = err.rawMessage?.replace(/.*\]\s*/, '').trim();
      }
      console.log(err);
      dispatch(
        setToastInfo({
          label: message,
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setUpdatingAssessment(false);
    }
  };

  // --------------------------------------------------------------

  const handleShowMediaClick = async (
    sectionIndex: number,
    index: number,
    altIdx: number
  ) => {
    await showMediaClick(sectionIndex, index, altIdx);
    // Update whether to show image in the question
    handleSave();
  };

  // --------------------------------------------------------------

  // Updated renderQuestionList with drag-and-drop
  const renderQuestionList = (
    questionList: SectionQuestionMetaData[],
    sectionIndex: number
  ) => {
    return (
      <Droppable droppableId={`section-${sectionIndex}`} type="MAIN_BOX">
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { md: pxTovW(40), xs: pxToRem(15) },
            }}
          >
            {questionList
              .sort((a, b) => a.sequence - b.sequence)
              .map((ques, index) => {
                const allQuestions = [
                  ques,
                  ...(ques.alternateQuestions
                    .sort((a, b) => a.sequence - b.sequence)
                    .map((altQuestion) => altQuestion) || []),
                ];
                const questions = allQuestions.map((ques) =>
                  getFormattedQuestionFromId(ques.questionId)
                );
                if (questions.length === 0) {
                  return null;
                }
                // Level 1 Draggable
                return (
                  <Draggable
                    key={ques.questionId}
                    draggableId={ques.questionId}
                    index={index}
                    isDragDisabled={
                      taskCreationStatus ===
                      TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
                    }
                  >
                    {(provided) => {
                      const firstQuestion = allQuestions[0];
                      const showMedia = firstQuestion.showMedia;
                      const questionData = qpg_assessment_content?.find(
                        (q) => q.questionId === ques.questionId
                      );

                      return (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                        >
                          {questions.length === 1 ? (
                            <QuestionContainerWithSolution
                              questionNumber={getQuestionNumberFromIndex(
                                sectionIndex,
                                index,
                                questionSectionMetaInfo
                              )}
                              hideImage={
                                taskCreationStatus ===
                                  TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED &&
                                !showMedia
                              }
                              headerLeftElement={
                                <QuestionSourceTags
                                  question={questionData}
                                  teacherProfileId={user_info?.teacherProfileId}
                                  teacherName={user_info?.userName || ''}
                                />
                              }
                              headerRightElement={
                                taskCreationStatus !==
                                TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED ? (
                                  <Box
                                    {...provided.dragHandleProps}
                                    sx={{ flex: 1 }}
                                  >
                                    <InteractiveButtons
                                      stackComponents={isMobile}
                                      showAIButton={questions[0]?.isAiGenerated}
                                      handleAIPrompt={(prompt) => {
                                        handleReplaceClick(
                                          sectionIndex,
                                          index,
                                          0,
                                          questions[0],
                                          true,
                                          [prompt]
                                        );
                                      }}
                                      isIncludeImageChecked={showMedia}
                                      showIncludeImageButton={questionHasImage(
                                        questions[0]
                                      )}
                                      handleIncludeImageClick={() => {
                                        handleShowMediaClick(
                                          sectionIndex,
                                          index,
                                          0
                                        );
                                      }}
                                      addAlternateQuestionsClick={() => {
                                        dispatch(
                                          setSelectedSectionIndexForAlternateQuestion(
                                            sectionIndex
                                          )
                                        );
                                        dispatch(
                                          setSelectedQuestionIndexForAlternateQuestion(
                                            index
                                          )
                                        );
                                        dispatch(
                                          setSelectedQuestionTypeForAlternateQuestion(
                                            questions[0]?.questionType
                                          )
                                        );
                                        navigate(
                                          `${QPG}${ASSESSMENT_QUESTIONBANK}`,
                                          {
                                            replace: true,
                                            state: {
                                              from: location.pathname,
                                              redirectionPath:
                                                location.state?.redirectionPath,
                                            },
                                          }
                                        );
                                      }}
                                      onDeleteClick={() => {
                                        setDeleteQuestionPopupState({
                                          sectionIndex,
                                          questionIndex: index,
                                          altIdx: 0,
                                        });
                                      }}
                                      handleReplaceClick={() =>
                                        handleReplaceClick(
                                          sectionIndex,
                                          index,
                                          0,
                                          questions[0],
                                          hasAiGeneratedQuestions
                                          // questions[0]?.isAiGenerated
                                        )
                                      }
                                    />
                                  </Box>
                                ) : (
                                  // Published state
                                  <Box {...provided.dragHandleProps}></Box>
                                )
                              }
                              question={questions[0]}
                              showQuestionStats
                              showAnswer
                              imageResizeEnabled={true}
                              key={'ques_' + index}
                            />
                          ) : (
                            <Box
                              sx={{
                                border: `1px solid #007CDC`,
                                borderRadius: {
                                  xs: pxToRem(20),
                                  md: pxTovW(20),
                                },
                                padding: {
                                  xs: `${pxTovW(14)} ${pxTovW(10)}`,
                                  md: `${pxTovW(18)} ${pxTovW(13)}`,
                                },
                                backgroundColor: '#F5F5FF',
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                              key={'alternate_ques_' + index}
                            >
                              <Box
                                sx={{ display: 'flex' }}
                                {...provided.dragHandleProps}
                              >
                                {taskCreationStatus !==
                                TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED ? (
                                  <Box
                                    sx={{
                                      ml: 'auto',
                                      pr: { xs: pxToRem(10), md: pxTovW(15) },
                                      pb: { xs: pxToRem(6), md: pxTovW(10) },
                                      pt: { xs: pxToRem(6), md: pxTovW(0) },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: pxTovW(14),
                                    }}
                                  >
                                    {/* Blue outer box */}
                                    <InteractiveButtons
                                      addAlternateQuestionsClick={() => {
                                        dispatch(
                                          setSelectedSectionIndexForAlternateQuestion(
                                            sectionIndex
                                          )
                                        );
                                        dispatch(
                                          setSelectedQuestionIndexForAlternateQuestion(
                                            index
                                          )
                                        );
                                        dispatch(
                                          setSelectedQuestionTypeForAlternateQuestion(
                                            questions[0]?.questionType
                                          )
                                        );
                                        navigate(
                                          `${QPG}${ASSESSMENT_QUESTIONBANK}`,
                                          {
                                            replace: true,
                                            state: {
                                              from: location.pathname,
                                              redirectionPath:
                                                location.state?.redirectionPath,
                                            },
                                          }
                                        );
                                      }}
                                      onDeleteClick={() => {
                                        setDeleteQuestionPopupState({
                                          sectionIndex,
                                          questionIndex: index,
                                        });
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box {...provided.dragHandleProps} />
                                )}
                              </Box>
                              {/* Level 2 DragDrop for main + alternates */}
                              <Droppable
                                droppableId={`alternates-${sectionIndex}-${index}`}
                                type={`ALTERNATE_QUESTION-${sectionIndex}-${index}`}
                                direction="vertical"
                              >
                                {(altProvided) => {
                                  return (
                                    <Box
                                      ref={altProvided.innerRef}
                                      {...altProvided.droppableProps}
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: {
                                          md: pxTovW(20),
                                          xs: pxToRem(10),
                                        },
                                      }}
                                    >
                                      {allQuestions.map((ques, altIdx, arr) => {
                                        const qid = ques.questionId;
                                        const question =
                                          getFormattedQuestionFromId(qid);
                                        const isAiGenerated =
                                          question?.isAiGenerated;
                                        const showMedia = ques.showMedia;
                                        return (
                                          <React.Fragment key={qid}>
                                            <Draggable
                                              key={qid}
                                              draggableId={`${qid}-alt`}
                                              index={altIdx}
                                              isDragDisabled={
                                                taskCreationStatus ===
                                                TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
                                              }
                                            >
                                              {(altDraggable) => (
                                                <Box
                                                  ref={altDraggable.innerRef}
                                                  {...altDraggable.draggableProps}
                                                >
                                                  <QuestionContainerWithSolution
                                                    questionNumber={getQuestionNumberFromIndex(
                                                      sectionIndex,
                                                      index,
                                                      questionSectionMetaInfo
                                                    )}
                                                    hideImage={
                                                      taskCreationStatus ===
                                                        TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED &&
                                                      !showMedia
                                                    }
                                                    headerLeftElement={
                                                      <QuestionSourceTags
                                                        question={question}
                                                        teacherProfileId={
                                                          user_info?.teacherProfileId
                                                        }
                                                        teacherName={
                                                          user_info?.userName ||
                                                          ''
                                                        }
                                                      />
                                                    }
                                                    headerRightElement={
                                                      taskCreationStatus !==
                                                      TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED ? (
                                                        <Box
                                                          {...altDraggable.dragHandleProps}
                                                          sx={{ flex: 1 }}
                                                        >
                                                          <InteractiveButtons
                                                            stackComponents={
                                                              isMobile
                                                            }
                                                            showAIButton={
                                                              isAiGenerated
                                                            }
                                                            handleAIPrompt={(
                                                              prompt
                                                            ) => {
                                                              handleReplaceClick(
                                                                sectionIndex,
                                                                index,
                                                                altIdx,
                                                                question,
                                                                true,
                                                                [prompt]
                                                              );
                                                            }}
                                                            showIncludeImageButton={questionHasImage(
                                                              question
                                                            )}
                                                            handleIncludeImageClick={() => {
                                                              handleShowMediaClick(
                                                                sectionIndex,
                                                                index,
                                                                altIdx
                                                              );
                                                            }}
                                                            isIncludeImageChecked={
                                                              showMedia
                                                            }
                                                            onDeleteClick={() => {
                                                              setDeleteQuestionPopupState(
                                                                {
                                                                  sectionIndex,
                                                                  questionIndex:
                                                                    index,
                                                                  altIdx:
                                                                    altIdx,
                                                                }
                                                              );
                                                            }}
                                                            handleReplaceClick={() =>
                                                              handleReplaceClick(
                                                                sectionIndex,
                                                                index,
                                                                altIdx,
                                                                question,
                                                                hasAiGeneratedQuestions
                                                                // isAiGenerated
                                                              )
                                                            }
                                                          />
                                                        </Box>
                                                      ) : (
                                                        // Published state
                                                        <Box
                                                          {...altDraggable.dragHandleProps}
                                                        />
                                                      )
                                                    }
                                                    question={question}
                                                    showQuestionStats
                                                    showAnswer
                                                    imageResizeEnabled={true}
                                                    key={
                                                      'ques_' +
                                                      index +
                                                      '-' +
                                                      altIdx
                                                    }
                                                  />
                                                </Box>
                                              )}
                                            </Draggable>
                                            {altIdx < arr.length - 1 &&
                                              renderOrBox()}
                                          </React.Fragment>
                                        );
                                      })}
                                      {altProvided.placeholder}
                                    </Box>
                                  );
                                }}
                              </Droppable>
                            </Box>
                          )}
                        </Box>
                      );
                    }}
                  </Draggable>
                );
              })}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    );
  };

  // --------------------------------------------------------------

  const getSectionStatement = (
    sectionQuestionsInfo: SectionQuestionMetaData[],
    sectionIndex: number
  ) => {
    const sectionQuestions = (sectionQuestionsInfo || [])
      .map((ques) => {
        const quesId =
          ques.questionId ||
          ques.alternateQuestions.find((val) => !!val.questionId)?.questionId;
        return quesId ? getFormattedQuestionFromId(quesId) : undefined;
      })
      .filter((val) => !!val) as Question[];
    const allQuestionsType = questionCountTypeWise(sectionQuestions);
    return Object.keys(allQuestionsType)
      ?.map((quesKey) => {
        const quesType = allQuestionsType[quesKey];
        const { type, marks, count } = quesType;
        const indexes = getAllIndexes(
          sectionQuestions,
          (ques) => ques.questionType == type
        );
        const compressedIndexes = compressRanges(indexes);
        const questionNumbers = compressedIndexes.map((range) => {
          return range.map((index) =>
            getQuestionNumberFromIndex(
              sectionIndex,
              index,
              questionSectionMetaInfo
            )
          );
        });
        const questionNumbersString = questionNumbers
          .map((num) => num.join(' to '))
          .join(', ');
        const marksEach =
          (count > 0 ? Math.round(marks / count) : undefined) || 0;
        const marksStatement = marksEach > 1 ? 'marks' : 'mark';
        switch (type) {
          case Question_QuestionEnum.QUESTION_TYPE_MCQS:
            return `Select and write one most appropriate option out of the four options given for each of the Questions ${questionNumbersString} each question carries ${marksEach} ${marksStatement}`;
          case Question_QuestionEnum.QUESTION_TYPE_MCQM:
            return `Select and write all the correct options out of the four options given for each of the Questions ${questionNumbersString} each question carries ${marksEach} ${marksStatement}`;
          case Question_QuestionEnum.QUESTION_TYPE_FIB:
            return `Fill in the blanks in each of the Questions ${questionNumbersString} with the most appropriate word(s). Each question carries ${marksEach} ${marksStatement}`;
          case Question_QuestionEnum.QUESTION_TYPE_MTF:
            return `Match the following Questions ${questionNumbersString} each question carries ${marksEach} ${marksStatement}`;
          case Question_QuestionEnum.QUESTION_TYPE_ARRANGE:
            return `Arrange the following Questions ${questionNumbersString} each question carries ${marksEach} ${marksStatement}`;
          case Question_QuestionEnum.QUESTION_TYPE_TF:
            return `Select either true or false for the Questions ${questionNumbersString} each question carries ${marksEach} ${marksStatement}`;
          case Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT:
            return `Question ${questionNumbersString} Very Short Answer Question carrying ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_SHORT:
            return `Question ${questionNumbersString} Short Answer Question carrying ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_LONG:
            return `Question ${questionNumbersString} Long Answer Question carrying ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_CBQ:
            return `Question ${questionNumbersString} Competency Based Question carrying ${marksEach} ${marksStatement} each`;
          case Question_QuestionEnum.QUESTION_TYPE_LABEL:
            return `Question ${questionNumbersString} Label question carrying ${marksEach} ${marksStatement} each`;
          default:
            return '';
        }
      })
      .join('. ');
  };

  // --------------------------------------------------------------

  const sectionMetaStatements = questionSectionMetaInfo.map((info, index) => {
    const sortedSectionQuestionsInfo = info.sectionQuestionsInfo.sort(
      (a, b) => a.sequence - b.sequence
    );
    return getSectionStatement(sortedSectionQuestionsInfo, index);
  });

  // --------------------------------------------------------------

  const renderSectionList = () => {
    return (
      <DragDropContext
        onDragEnd={(result) => {
          // Detect if it's level 1 or 2
          if (result.type === 'MAIN_BOX') {
            // sectionIndex from droppableId
            const match = result.source.droppableId.match(/section-(\d+)/);
            if (match) {
              onDragEnd(result, parseInt(match[1]));
            }
          } else if (result.type.startsWith('ALTERNATE_QUESTION')) {
            onDragEnd(result);
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { md: pxTovW(20), xs: pxToRem(20) },
          }}
        >
          {questionSectionMetaInfo.map((info, index) => {
            const sortedSectionQuestionsInfo = info.sectionQuestionsInfo.sort(
              (a, b) => a.sequence - b.sequence
            );
            return (
              <Box
                key={index}
                sx={{
                  border: '1px solid #E0E2E4',
                  borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
                  background: '#FFFFFF',
                  boxShadow: `0px 0px ${pxToRem(5)} #E7E7E7D9`,
                  padding: {
                    md: `${pxToRem(20)} ${pxToRem(25)}`,
                    xs: `${pxToRem(16)} ${pxToRem(7)}`,
                  },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { md: pxTovW(20), xs: pxToRem(12) },
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color={'#007CDC'}>
                    Section {numberToAlphabet(index + 1)}
                  </Typography>
                </Box>
                {sectionMetaStatements[index].length > 0 && (
                  <Typography
                    variant="bodyText"
                    sx={{ textAlign: 'center', fontWeight: 'bold' }}
                  >
                    {sectionMetaStatements[index]}
                  </Typography>
                )}
                {renderQuestionList(sortedSectionQuestionsInfo, index)}
              </Box>
            );
          })}
        </Box>
      </DragDropContext>
    );
  };

  // --------------------------------------------------------------

  const printablePaperRef = useRef<any>(null);

  const handleSave = async (
    updateInstructions: boolean = false,
    updateTitle: boolean = false,
    resetInstructions: boolean = false,
    shouldPublish: boolean = false,
    createCopy: boolean = false,
    qpgQuestionTypeMarks?: QuestionTypeMarks[]
  ) => {
    try {
      const instructions =
        hasCustomInstructions &&
        testLevelInstructions.length > 0 &&
        !resetInstructions
          ? testLevelInstructions
          : [];
      setUpdatingAssessment(true);
      const assessmentCreationSubmitResponse =
        await LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler.assessmentCreationSubmit(
          {
            assessmentId: createCopy
              ? undefined
              : qpgAssessmentTask?.assessmentId,
            sourceGeneoAssessmentId: createCopy
              ? qpgAssessmentTask?.assessmentId
              : undefined,
            teacherId: user_info?.teacherProfileId,
            subjectId: qpgAssessmentTask?.subjectId,
            sectionId: qpgAssessmentTask?.sectionId,
            questionTypeAndMarks:
              qpgQuestionTypeMarks || qpg_question_type_marks,
            taskName: updateTitle
              ? assessmentName
              : createCopy
              ? (qpgAssessmentTask?.assessmentTitle || '') + ' (Copy)'
              : qpgAssessmentTask?.assessmentTitle,
            category: Module_ModuleCategoryEnum.MODULE_CATEGORY_BOOK,
            instructions: updateInstructions
              ? instructions
              : qpgAssessmentTask?.assessmentInstructions,
            creationStatus: shouldPublish
              ? TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
              : TaskCreationStatusEnum.TASK_CREATION_STATUS_DRAFT,
            taskType: TaskEnum.TASK_QUESTION_PAPER_CUSTOM,
            chapterIdsInfo:
              qpgAssessmentTask?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo,
            questionModuleMap: questionModuleMap
              .filter((val) =>
                questionsListWithoutAlternateQuestions.some(
                  (ques) => ques.questionId === val.questionId
                )
              )
              .filter(
                (val, index, self) =>
                  index ===
                  self.findIndex((t) => t.questionId === val.questionId)
              ),
            questionSectionMetaInfo: (questionSectionMetaInfo || []).filter(
              (info) =>
                info.sectionQuestionsInfo.length > 0 &&
                info.sectionQuestionsInfo.some(
                  (ques) =>
                    ques.questionId !== undefined ||
                    (Array.isArray(ques.alternateQuestions) &&
                      ques.alternateQuestions?.length > 0)
                )
            ),
          }
        );
      if (
        createCopy &&
        assessmentCreationSubmitResponse.data?.assessment?.assessmentId
      ) {
        navigate(
          `${QPG}${ASSESSMENT_REVIEW}/${assessmentCreationSubmitResponse.data?.assessment?.assessmentId}`,
          {
            state: {
              redirectionPath: location.state?.redirectionPath,
            },
            replace: true,
          }
        );
        dispatch(
          setToastInfoV2({
            label: 'Assessment copy created successfully',
            variant: 'info',
            open: true,
          })
        );
        return;
      }
      dispatch(
        setQPGQuestionsSequenceInfoHash(
          generateHashString(new TaskInfo(qpgQuestionsSequenceInfo))
        )
      );
      const instructionsHash = getStringHash(instructions.join('\n'));
      dispatch(setQPGAssessmentInstructionsHash(instructionsHash));
      if (shouldPublish) {
        qpgAssessmentTask.taskCreationStatus =
          TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED;
      }
      if (updateTitle) {
        qpgAssessmentTask.assessmentTitle = assessmentName;
      }
      if (resetInstructions) {
        qpgAssessmentTask.assessmentInstructions = [];
      }
      dispatch(setQPGAssessmentTask(qpgAssessmentTask));
      dispatch(
        setToastInfoV2({
          label: shouldPublish
            ? 'Your assessment has been published'
            : updateTitle
            ? 'Assessment name updated successfully'
            : updateInstructions
            ? 'Assessment instructions updated successfully'
            : 'Assessment updated successfully',
          variant: shouldPublish ? 'info' : 'success',
          open: true,
        })
      );
    } catch (error) {
      console.error('Error saving question paper:', error);
      dispatch(
        setToastInfoV2({
          label:
            error instanceof ConnectError
              ? error.rawMessage?.replace(/.*\]\s*/, '').trim()
              : 'Error while saving assessment',
          variant: 'error',
          open: true,
        })
      );
    } finally {
      setUpdatingAssessment(false);
      if (shouldPublish) {
        setAssessmentNameEditMode(false);
        setBloomOpen(false);
        setSelectedOpen(false);
        setInstructionsOpen(false);
      }
    }
  };
  const handleMarksSave = async (
    marksArray: { questionType: number; marks: number }[]
  ) => {
    const tempmarksArray = qpg_question_type_marks?.map((item) => {
      const newItem = new QuestionTypeMarks(item);
      newItem.marks =
        marksArray.find((m) => m.questionType === item.questionType)?.marks ||
        item.marks;
      return newItem;
    });
    console.log('Updated marks array to be saved:', tempmarksArray);

    dispatch(setQPGQuestionTypeMarks(tempmarksArray));

    await handleSave(false, false, false, false, false, tempmarksArray);
  };
  // --------------------------------------------------------------

  if (loading === 'loading') return <Loader />;

  const canRenderCustomInstructions =
    hasCustomInstructions &&
    testLevelInstructions.length > 0 &&
    testLevelInstructions[0].length > 0;

  return (
    <Box
      sx={{
        ...styles.root,
        pointerEvents: updatingAssessment ? 'none' : 'auto',
        opacity: updatingAssessment ? 0.5 : 1,
      }}
    >
      {isMobile ? (
        <Box sx={{ paddingX: pxToRem(20), paddingTop: pxToRem(10) }}>
          {' '}
          <Typography variant="h1">Question Paper Generator</Typography>
        </Box>
      ) : (
        <></>
      )}
      <Box
        sx={{
          display: 'flex',
          boxSizing: 'border-box',
          position: { xs: 'fixed', md: 'relative' },
          zIndex: { xs: 100, md: 0 },
          bottom: { xs: '0', md: 'unset' },
          width: '100%',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: pxToRem(20), md: 0 },
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: { xs: 'common.white', md: 'inherit' },
          padding: { xs: pxToRem(20), lg: `${pxTovW(20)} ${pxTovW(240)}` },
        }}
      >
        {isMobile ? (
          <></>
        ) : (
          <Typography variant="h1">Question Paper Generator</Typography>
        )}
        {taskCreationStatus ===
          TaskCreationStatusEnum.TASK_CREATION_STATUS_DRAFT && (
          <Box
            sx={{
              display: 'flex',
              width: { xs: '100%', md: 'unset' },
              flexDirection: 'column',
              gap: { xs: pxToRem(10), md: pxTovW(15) },
              alignItems: 'center',
            }}
          >
            <PrimaryButton
              sx={{
                height: { xs: pxToRem(40), md: pxTovW(59) },
                width: { xs: '100%', md: pxTovW(384) },
              }}
              disabled={
                assessmentName.length == 0 ||
                questionSectionMetaInfo.length === 0
              }
              onClick={async () => {
                // Publish the assessment
                handleSave(true, true, false, true);
                await interactionEvent({
                  context: 'Question_paper_generator',
                  name: 'PUBLISH',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  color: 'white',
                }}
              >
                Publish
              </Typography>
            </PrimaryButton>
            <Typography variant="smallText" sx={{ color: '#5C5A5A' }}>
              <span style={{ fontWeight: 'bold', color: '#5C5A5A' }}>
                Note:
              </span>{' '}
              Published assessments cannot be edited.
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={styles.mainContainer}>
        <Box sx={styles.leftPanel}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: { xs: pxToRem(20), md: 0 },
              gap: { xs: pxToRem(20), md: pxTovW(20) },
            }}
          >
            {taskCreationStatus !==
              TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED && (
              <Box
                sx={{
                  width: { xs: '100%', md: '100%' },
                  display: 'flex',
                  gap: { xs: pxToRem(5), md: pxTovW(13) },
                  background: '#D2EBFF',
                  borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
                  cursor: 'pointer',
                }}
                onClick={async () => {
                  navigate(`${QPG}${ASSESSMENT_QUESTIONBANK}`, {
                    replace: true,
                    state: {
                      from: location.pathname,
                      redirectionPath: location.state?.redirectionPath,
                    },
                  });
                  await interactionEvent({
                    context: 'Navigation_bar',
                    name: 'ADD_FROM_QUESTION_BANK',
                    pathSegments: pathSegments,
                    isOffline: isOffline,
                  });
                }}
              >
                <ImageWrapper
                  name="questionBank"
                  type="png"
                  parentFolder="icons"
                  styles={{
                    width: { xs: pxToRem(65), md: pxTovW(110), lg: pxTovW(81) },
                    height: 'auto',
                    marginY: 'auto',
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginY: 'auto',
                    gap: { xs: pxToRem(4), md: pxTovW(5) },
                  }}
                >
                  <Box
                    sx={{
                      width: 'max-content',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      background: '#1674BF',
                      padding: {
                        xs: `${pxToRem(3)} ${pxToRem(12)} ${pxToRem(4)}`,
                        md: `${pxTovW(5)} ${pxTovW(15)} ${pxTovW(6)}`,
                      },
                      borderRadius: {
                        xs: pxToRem(10),
                        md: pxTovW(20),
                        lg: pxTovW(17),
                      },
                    }}
                  >
                    <Typography
                      variant="smallText"
                      color={'common.white'}
                      fontWeight={'600'}
                    >
                      1000+ Questions
                    </Typography>
                  </Box>
                  <Typography
                    variant={isMobile ? 'h3' : isIpad ? 'h5' : 'h4'}
                    fontWeight={'600'}
                    fontFamily={'poppins'}
                  >
                    Add from Question Bank
                  </Typography>
                </Box>

                <Box
                  sx={{
                    marginLeft: 'auto',
                    marginY: 'auto',
                    marginRight: { xs: pxToRem(15), md: pxTovW(20) },
                  }}
                >
                  <ImageWrapper
                    styles={{
                      width: { xs: pxToRem(23), md: pxTovW(30) },
                      height: { xs: pxToRem(24), md: pxTovW(30) },
                    }}
                    name="right-arrow-circle"
                    type="png"
                    parentFolder="illustrations"
                  />
                </Box>
              </Box>
            )}
            <Box sx={styles.AssessmentNameContainer}>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant={'h3'}
                  fontWeight={'600'}
                  fontFamily={'poppins'}
                >
                  Assessment Name
                </Typography>
                {taskCreationStatus !==
                  TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED && (
                  <>
                    {assessmentNameEditMode === false ? (
                      <ImageWrapper
                        name="edit"
                        type="png"
                        parentFolder="icons"
                        styles={{
                          width: { xs: pxToRem(24), md: pxTovW(24) },
                          height: { xs: pxToRem(23), md: pxTovW(23) },
                          cursor: 'pointer',
                        }}
                        onClick={async () => {
                          setAssessmentNameEditMode(true);
                          await interactionEvent({
                            context: 'Assessment_name',
                            name: 'EDIT',
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          });
                        }}
                      />
                    ) : (
                      <PrimaryButton
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: { xs: pxToRem(40), md: pxTovW(50) },
                          height: { xs: pxToRem(20), md: pxTovW(23) },
                          borderRadius: { xs: pxToRem(5), md: pxTovW(5) },
                        }}
                        disabled={assessmentName.length == 0}
                        onClick={async () => {
                          setAssessmentNameEditMode(false);
                          // update the assessment name
                          handleSave(false, true);
                          await interactionEvent({
                            context: 'Assessment_name',
                            name: 'SAVE',
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          });
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 'bold',
                            color: 'white',
                          }}
                        >
                          Save
                        </Typography>
                      </PrimaryButton>
                    )}
                  </>
                )}
              </Box>
              <Input
                disabled={!assessmentNameEditMode}
                disableUnderline
                value={assessmentName}
                onChange={(e) => setAssessmentName(e.target.value)}
                type="text"
                sx={{
                  width: '100%',
                  backgroundColor: '#F8F8F8',
                  border: '1.5px solid #E2E2E2',
                  height: isMobile ? pxToRem(40) : pxTovW(59),
                  borderRadius: isMobile ? pxToRem(5) : pxTovW(10),
                  padding: isMobile ? pxToRem(5) : pxTovW(5),
                  '& .MuiInput-input.Mui-disabled': {
                    color:
                      taskCreationStatus ==
                      TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
                        ? '#000'
                        : undefined,
                    WebkitTextFillColor:
                      taskCreationStatus ==
                      TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED
                        ? '#000'
                        : undefined,
                  },
                }}
              />
            </Box>
          </Box>

          <Box>
            <Box
              sx={{
                width: '100%',
                display: { xs: 'none', md: 'flex' },
              }}
            >
              <SelectedChapters
                // editClickHandler={() => console.log('editClickHandler')}
                assessmentTaskInfo={qpgAssessmentTask.taskMetaInfo}
                questionModuleMap={questionModuleMap
                  .filter((val) =>
                    questionsListWithoutAlternateQuestions.some(
                      (ques) => ques.questionId === val.questionId
                    )
                  )
                  .filter(
                    (val, index, self) =>
                      index ===
                      self.findIndex((t) => t.questionId === val.questionId)
                  )}
              />
            </Box>
          </Box>
          <Box sx={styles.BloomContainer}>
            <Box>
              <BloomLevelComponent bloomLevelData={bloomLevelData} />
            </Box>
            <Divider sx={{ bgcolor: '#E0E0E0', height: '1px', opacity: 0.5 }} />
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <InfoBarComponent contentList={infoBarContentList} />
            </Box>
            <Divider sx={{ bgcolor: '#E0E0E0', height: '1px', opacity: 0.5 }} />
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography
                variant="h3"
                fontWeight={600}
                fontFamily={'poppins'}
                marginBottom={pxToRem(10)}
              >
                Difficulty Level Breakdown
              </Typography>
              <InfoBarComponent contentList={getDifficultyLevelstats()} />
            </Box>
            <Divider sx={{ bgcolor: '#E0E0E0', height: '1px', opacity: 0.5 }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { md: pxTovW(20) },
              }}
            >
              <Typography variant="h4" fontWeight={600} fontFamily={'poppins'}>
                Question Type
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: pxToRem(20) },
                }}
              >
                {renderQuestionTypeList()}
              </Box>
            </Box>
          </Box>
          <Box sx={styles.BloomContainer}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: {
                  xs: pxToRem(20),
                  md: pxTovW(20),
                },
              }}
            >
              <EditMarksPanel
                questions={questionsListWithoutAlternateQuestions}
                isMobile={isMobile}
                onSave={handleMarksSave}
              />
            </Box>
          </Box>
          <Box sx={styles.mobileContainer}>
            <Typography
              variant={isMobile ? 'h2' : 'h3'}
              sx={{
                fontWeight: 'bold',
              }}
            >
              What's Covered
            </Typography>
            <IconWrapper
              name="view"
              type="png"
              parentFolder="icons"
              size="md"
              customSx={{
                height: { xs: pxToRem(20) },
                width: { xs: pxToRem(20) },
              }}
              onClick={() => setSelectedOpen(true)}
            />
          </Box>

          <Box sx={styles.mobileContainer}>
            <Typography
              variant={isMobile ? 'h2' : 'h3'}
              sx={{
                fontWeight: 'bold',
              }}
            >
              Bloom Level
            </Typography>
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                gap: { xs: pxToRem(10) },
              }}
            >
              <IconWrapper
                name="view"
                type="png"
                parentFolder="icons"
                size="large"
                customSx={{
                  height: { xs: pxToRem(20) },
                  width: { xs: pxToRem(20) },
                }}
                onClick={() => {
                  setBloomOpen(true);
                }}
              />
            </Box>
          </Box>
          <Box sx={styles.mobileContainer}>
            <Typography
              variant={isMobile ? 'h2' : 'h3'}
              sx={{
                fontWeight: 'bold',
              }}
            >
              Edit Marks
            </Typography>
            <IconWrapper
              name="view"
              type="png"
              parentFolder="icons"
              size="md"
              customSx={{
                height: { xs: pxToRem(20) },
                width: { xs: pxToRem(20) },
              }}
              onClick={() => setEditMarksOpen(true)}
            />
          </Box>
        </Box>
        <Box sx={styles.rightPanel}>
          <Box
            sx={{
              padding: { xs: pxToRem(20), md: 0 },
              display: 'flex',
              flexDirection: 'column',
              gap: { md: pxTovW(20), xs: pxToRem(20) },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: { xs: '5px', md: '10px' },
              }}
            >
              <Typography variant="h2">Review Question Paper</Typography>

              {questionSectionMetaInfo.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: '6px', md: '20px' },
                  }}
                >
                  <Box
                    sx={{
                      p: {
                        xs: `${pxToRem(8)} ${pxToRem(10)}`,
                        md: `${pxTovW(10)} ${pxTovW(12)}`,
                      },
                      borderRadius: { xs: pxToRem(5), md: pxTovW(10) },
                      border: '1px solid #0AA34F',
                      cursor: 'pointer',
                      background: '#CFFFE5',
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: pxToRem(5), md: pxTovW(10) },
                    }}
                    onClick={async () => {
                      if (isAssessmentChanged) {
                        // Save button click. Usually save button won't appear as we are
                        // updating the assessment on every change in assessment hash
                        // Only appears if previous api fails
                        handleSave();
                      } else {
                        dispatch(
                          setModeOfConduction(
                            getModeOfConductionEnum('Geneo App')
                          )
                        );
                        navigate(`${ASSESMENT_LIBRARY}`, {
                          state: {
                            fromQPG: true,
                            redirectionPath: location.state?.redirectionPath,
                          },
                        });
                      }
                      await interactionEvent({
                        context: 'Question_paper_generator',
                        name: isAssessmentChanged ? 'SAVE' : 'VIEW_LIBRARY',
                        pathSegments: pathSegments,
                        isOffline: isOffline,
                      });
                    }}
                  >
                    <IconWrapper
                      name="plus_qpg_green"
                      customSx={{ height: '23px', width: '23px' }}
                      type="png"
                      parentFolder="icons"
                    />
                    <Typography
                      variant="cardText"
                      color={'#0AA34F'}
                      fontWeight={'600'}
                    >
                      {isAssessmentChanged
                        ? 'Save'
                        : isMobile
                        ? 'Library'
                        : 'View Library'}
                    </Typography>
                  </Box>
                  {taskCreationStatus ===
                    TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED && (
                    <Box
                      aria-describedby={'download-pdf-popover'}
                      onClick={async (event) => {
                        handlePopoverClick(event);
                        await interactionEvent({
                          context: 'Published_question_paper',
                          name: 'DOWNLOAD_PDF',
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        });
                      }}
                      sx={{
                        p: {
                          xs: `${pxToRem(8)} ${pxToRem(10)}`,
                          md: `${pxTovW(10)} ${pxTovW(12)}`,
                        },
                        borderRadius: { xs: pxToRem(5), md: pxTovW(10) },
                        border: '1px solid #007CDC',
                        cursor: 'pointer',
                        background: '#E1F2FF',
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: pxToRem(5), md: pxTovW(10) },
                      }}
                    >
                      <IconWrapper
                        name="download-qpg-blue"
                        customSx={{ height: '23px', width: '23px' }}
                        type="png"
                        parentFolder="icons"
                      />
                      {!isMobile && (
                        <Typography
                          variant="cardText"
                          color={'#007CDC'}
                          fontWeight={'600'}
                        >
                          Download Pdf
                        </Typography>
                      )}
                    </Box>
                  )}

                  <Popover
                    id={'download-pdf-popover'}
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    sx={{
                      '& .MuiPopover-paper': {
                        borderRadius: { xs: pxToRem(15), md: pxTovW(15) },
                        display: 'flex',
                        flexDirection: 'column',
                        py: { xs: pxToRem(3), md: pxTovW(5) },
                      },
                    }}
                  >
                    {[
                      {
                        name: 'Download Question Paper (PDF)',
                        eventName: 'DOWNLOAD_QUESTION_PAPER',
                        onClick: () => {
                          setPrintAnswer(false);
                          setTimeout(async () => {
                            await printablePaperRef.current?.handlePrint(
                              false,
                              qpgAssessmentTask?.assessmentTitle
                            );
                          }, 0);
                        },
                      },
                      {
                        name: 'Download Answer Paper (PDF)',
                        eventName: 'DOWNLOAD_ANSWER_PAPER',
                        onClick: () => {
                          setPrintAnswer(true);
                          setTimeout(async () => {
                            await printablePaperRef.current?.handlePrint(
                              true,
                              qpgAssessmentTask?.assessmentTitle
                            );
                          }, 0);
                        },
                      },
                      {
                        name: 'Download Question Paper (Doc)',
                        eventName: 'DOWNLOAD_QUESTION_PAPER_DOC',
                        onClick: () => {
                          setPrintAnswer(false);
                          setTimeout(async () => {
                            await printablePaperRef.current?.handlePrint(
                              false,
                              qpgAssessmentTask?.assessmentTitle,
                              true
                            );
                          }, 0);
                        },
                      },
                      {
                        name: 'Download Answer Paper (Doc)',
                        eventName: 'DOWNLOAD_ANSWER_PAPER_DOC',
                        onClick: () => {
                          setPrintAnswer(true);
                          setTimeout(async () => {
                            await printablePaperRef.current?.handlePrint(
                              true,
                              qpgAssessmentTask?.assessmentTitle,
                              true
                            );
                          }, 0);
                        },
                      },
                      {
                        name: 'Download Both (PDF)',
                        eventName: 'DOWNLOAD_BOTH',
                        onClick: () => {
                          setPrintAnswer(false);
                          setTimeout(async () => {
                            await printablePaperRef.current?.handlePrint(
                              false,
                              qpgAssessmentTask?.assessmentTitle
                            );
                            setPrintAnswer(true);
                            await printablePaperRef.current?.handlePrint(
                              true,
                              qpgAssessmentTask?.assessmentTitle
                            );
                          }, 0);
                        },
                      },
                    ].map((item, index, arr) => (
                      <Typography
                        key={item.name}
                        sx={{
                          px: 2,
                          cursor: 'pointer',
                          py: { xs: pxToRem(3), md: pxTovW(5) },
                          borderBottom:
                            index < arr.length - 1
                              ? '1px solid #DFDFDF'
                              : undefined,
                        }}
                        onClick={async () => {
                          item.onClick();
                          handlePopoverClose();
                          await interactionEvent({
                            context: 'Download_PDF',
                            name: item.eventName,
                            pathSegments: pathSegments,
                            isOffline: isOffline,
                          });
                        }}
                      >
                        {item.name}
                      </Typography>
                    ))}
                  </Popover>
                </Box>
              )}
            </Box>
            {hasAiGeneratedQuestions && (
              <Typography
                variant="smallText"
                sx={{
                  color: '#5C5A5A',
                  fontSize: { xs: pxToRem(12), md: pxToRem(14) },
                  width: '100%',
                }}
              >
                <span style={{ fontWeight: 'bold', color: '#5C5A5A' }}>
                  Note:
                </span>{' '}
                AI-generated questions may sometimes be inaccurate due to
                hallucinations. Please review and verify all questions before
                publishing to ensure accuracy.
              </Typography>
            )}
            {taskCreationStatus ===
              TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED && (
              <Box
                sx={{
                  display: 'flex',
                  boxSizing: 'border-box',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  // height: { xs: pxToRem(50), md: pxTovW(144), lg: pxTovW(74) },
                  backgroundColor: '#DBFFEB',
                  border: '1.5px solid #3BEA8A',
                  borderRadius: { xs: pxToRem(5), md: pxTovW(10) },
                  paddingY: { xs: pxToRem(15), md: pxTovW(15) },
                  paddingX: { xs: pxToRem(10), md: pxTovW(10) },
                }}
              >
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'left',
                      height: {
                        xs: pxToRem(100),
                        md: pxTovW(144),
                        lg: pxTovW(74),
                      },
                      gap: { xs: pxToRem(2), md: pxTovW(5) },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      <ImageWrapper
                        name="green-circle-check"
                        type="png"
                        parentFolder="icons"
                        styles={{
                          width: { xs: pxToRem(15), md: pxTovW(34) },
                          height: { xs: pxToRem(15), md: pxTovW(34) },
                        }}
                      />
                      <Typography
                        variant="h3"
                        color={'#0F8F10'}
                        fontWeight={'600'}
                      >
                        Published
                      </Typography>
                    </Box>
                    <Typography
                      variant="button"
                      sx={{
                        color: '#5C5A5A',

                        width: '100%',
                      }}
                    >
                      <span style={{ fontWeight: 'bold', color: '#5C5A5A' }}>
                        Note:
                      </span>{' '}
                      Published assessments cannot be edited. Create copy to
                      make changes.
                    </Typography>
                  </Box>
                </Box>
                <PrimaryButton
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: {
                      xs: pxToRem(220),

                      md: pxToRem(140),
                      lg: pxTovW(180),
                    },
                    height: {
                      xs: pxToRem(30),
                      md: pxTovW(72),
                      lg: pxTovW(42),
                    },
                    gap: { xs: pxToRem(5), md: pxTovW(10) },
                    backgroundColor: '#0AA34F',
                    borderRadius: { xs: pxToRem(5), md: pxTovW(7) },
                    '&:disabled': {
                      backgroundColor: '#0AA34F',
                      color: '#fff',
                      opacity: 0.5,
                    },
                  }}
                  onClick={async () => {
                    // create copy of published assessment
                    await handleSave(false, false, false, false, true);
                    await interactionEvent({
                      context: 'Review_question_paper',
                      name: 'CREATE_COPY',
                      pathSegments: pathSegments,
                      isOffline: isOffline,
                    });
                  }}
                >
                  <ImageWrapper
                    name="content"
                    type="png"
                    parentFolder="icons"
                    styles={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: { xs: pxToRem(11), md: pxTovW(20) },
                      height: { xs: pxToRem(12), md: pxTovW(20) },
                    }}
                  />
                  <Typography
                    variant="smallText"
                    fontWeight="bold"
                    color="white"
                  >
                    Create Copy
                  </Typography>
                </PrimaryButton>
              </Box>
            )}

            {renderTestLevelInstructions()}
            <PrintableQuestionPaper
              questionSectionMetaInfo={questionSectionMetaInfo}
              sectionMetaStatements={sectionMetaStatements}
              questionsList={questionsList(true)}
              printAnswer={printAnswer}
              ref={printablePaperRef}
              instructionsHeading={
                canRenderCustomInstructions
                  ? 'Test Instructions'
                  : 'General Instructions'
              }
              instructions={
                canRenderCustomInstructions
                  ? testLevelInstructions
                  : generalInstructions
              }
              loading={updatingAssessment}
              setLoading={setUpdatingAssessment}
              schoolName={user_info?.schoolDetails[0]?.schoolName}
              assessmentTitle={qpgAssessmentTask?.assessmentTitle}
              grade={qpgAssessmentTask?.class}
              section={qpgAssessmentTask?.section}
              subject={qpgAssessmentTask?.subject}
              SchoolLogo={school_logo_url}
            />
            {renderSectionList()}
          </Box>
        </Box>
      </Box>

      {renderChapterTopicPopup()}

      {renderBloomLevelPopup()}
      {renderEditMarksPopup()}

      <EditWarningPopup
        open={!!warningPopupOpen}
        handleClose={() => {
          setWarningPopupOpen(undefined);
        }}
        yesClickHandler={() => {
          handleBackClick(warningPopupOpen === 'home');
          return;
        }}
        noClickHandler={() => {
          setWarningPopupOpen(undefined);
        }}
        iconname={'back'}
        text1={`If you go ${
          warningPopupOpen === 'home' ? 'to home' : 'back'
        }, you may lose your changes.`}
        text2={`Are you sure you want go ${
          warningPopupOpen === 'home' ? 'to home' : 'back'
        }?`}
      />

      <ActionsPopup
        open={!!deleteQuestionPopupState}
        fontSmall={true}
        iconName="delete"
        popupText="Are you sure you want to delete this question"
        handleClose={() => setDeleteQuestionPopupState(undefined)}
        noClickHandler={() => setDeleteQuestionPopupState(undefined)}
        yesClickHandler={async () => {
          if (!deleteQuestionPopupState) {
            setDeleteQuestionPopupState(undefined);
            return;
          }
          if (deleteQuestionPopupState?.altIdx) {
            await deleteQuestion(
              deleteQuestionPopupState.sectionIndex,
              deleteQuestionPopupState.questionIndex,
              deleteQuestionPopupState.altIdx
            );
          } else {
            await deleteQuestion(
              deleteQuestionPopupState.sectionIndex,
              deleteQuestionPopupState.questionIndex
            );
          }
          dispatch(
            setToastInfo({
              label: 'Question deleted successfully',
              variant: 'info',
              open: true,
            })
          );
          setDeleteQuestionPopupState(undefined);
        }}
      />
    </Box>
  );
}
